/*
 * Picks a lighter mp4 rendition for a Shopify-hosted video.
 *
 * Liquid only exposes the largest mp4 of a video (often 1080p, several MB for a
 * few seconds) plus an HLS playlist. The playlist lists every rendition Shopify
 * encoded (480p / 720p / 1080p), and each HLS variant has an mp4 twin:
 *   <id>.HD-720p.4.5Mbps.hls.<n>_.m3u8  ->  <id>.HD-720p-4.5Mbps-<n>.mp4
 * The tiny playlist is read ahead of time so the chosen URL is ready before
 * playback starts (iOS only allows play() inside the user's gesture). Any
 * failure falls back to the mp4 from Liquid, and a load error on the derived
 * file switches back to it as well.
 *
 * Markup: <video data-src="<mp4 from Liquid>" data-hls="<m3u8 from Liquid>">
 */
(() => {
  const playlists = new Map();

  const toMp4 = (variantUri, fallbackMp4) => {
    const match = variantUri.match(/^([^/?]+)\.((?:HD|SD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8/);
    if (!match) return null;
    const queryIndex = fallbackMp4.indexOf('?');
    const path = queryIndex === -1 ? fallbackMp4 : fallbackMp4.slice(0, queryIndex);
    const query = queryIndex === -1 ? '' : fallbackMp4.slice(queryIndex);
    const base = path.slice(0, path.lastIndexOf('/') + 1);
    return `${base}${match[1]}.${match[2]}-${match[3]}-${match[4]}.mp4${query}`;
  };

  const loadVariants = (hlsUrl) => {
    if (!playlists.has(hlsUrl)) {
      playlists.set(
        hlsUrl,
        fetch(hlsUrl, { credentials: 'omit' })
          .then((response) => (response.ok ? response.text() : ''))
          .then((text) => {
            const lines = text.split('\n').map((line) => line.trim());
            const variants = [];
            lines.forEach((line, index) => {
              const resolution = line.startsWith('#EXT-X-STREAM-INF') && line.match(/RESOLUTION=(\d+)x(\d+)/);
              if (resolution && lines[index + 1]) {
                variants.push({ height: Math.min(+resolution[1], +resolution[2]), uri: lines[index + 1] });
              }
            });
            return variants.sort((a, b) => a.height - b.height);
          })
          .catch(() => [])
      );
    }
    return playlists.get(hlsUrl);
  };

  window.videoRendition = {
    // Resolves the best mp4 up to maxHeight and remembers it on the element.
    prepare(video, maxHeight) {
      if (video.dataset.renditionSrc) return Promise.resolve(video.dataset.renditionSrc);
      if (!video.dataset.hls) return Promise.resolve(video.dataset.src);
      return loadVariants(video.dataset.hls).then((variants) => {
        const fit = variants.filter((variant) => variant.height <= maxHeight).pop() || variants[0];
        const src = (fit && toMp4(fit.uri, video.dataset.src)) || video.dataset.src;
        video.dataset.renditionSrc = src;
        return src;
      });
    },

    // Sets the video source once: the prepared rendition if ready, else the Liquid mp4.
    attach(video) {
      if (video.getAttribute('src')) return;
      const fallback = video.dataset.src;
      const src = video.dataset.renditionSrc || fallback;
      if (src !== fallback) {
        video.addEventListener(
          'error',
          () => {
            if (video.getAttribute('src') === fallback) return;
            video.src = fallback;
            const promise = video.play();
            if (promise) promise.catch(() => {});
          },
          { once: true }
        );
      }
      video.src = src;
    },
  };
})();
